import React from 'react';
import {useMutation} from 'react-apollo';
import styled from 'styled-components';
import i18next from 'i18next';

import {Flex, Base} from 'web-app/react/components/layout/layout';
import {s1, s2, s3, s6, s8, s9, s20, getSpacing, s4} from 'web-app/styleguide/spacing';
import Button, {ButtonAppearance} from 'web-app/react/components/form-elements/button/button';
import {Subheader, Body} from 'web-app/react/components/text/text';
import {Backward} from 'web-app/react/components/icons/legacy/icons';
import * as Breakpoints from 'web-app/styleguide/breakpoint-helpers';
import * as LoginActions from 'web-app/react/login/actions';
import * as LoginHelpers from 'web-app/react/login/helpers';
import {ChallengeContext} from 'web-app/react/login/models';
import * as SentryService from 'web-app/services/sentry';
import Notice, {NoticeSeverity} from 'web-app/react/components/form-elements/notice/notice';
import ProfileImage, {ProfileImageProps} from 'web-app/react/components/profile-image/profile-image';
import {getApolloClient} from 'web-app/api/clients/famly-app';
import ApiHelper from 'web-app/services/api-helper';
import {getTitleForTarget, getImageUrlForTarget} from 'web-app/react/top-bar/profile-menu/user-context-helpers';
import {useFilteredUserContexts} from 'web-app/react/top-bar/profile-menu/use-filtered-user-contexts';
import LocalStorage from 'web-app/services/local-storage';
import TextField from 'web-app/react/components/form-elements/text-field/text-field';

import * as Queries from './queries';
import TwoFactorInput from './two-factor-input';
import {AnswerChallengeMutation, AnswerChallengeMutationVariables} from './__generated__/queries.api-types';

/*
/----------------------------------------------
/ Context list item component
/----------------------------------------------
*/

const ItemProfileImage = styled(ProfileImage)`
    flex-shrink: 0;
    width: unset;
`;

interface ContextSelectionItemProps {
    type: string;
    title: string;
    imageURLs?: ProfileImageProps['src'];
    onClick: VoidFunction;
}

const ContextSelectionItem: React.FC<React.PropsWithChildren<ContextSelectionItemProps>> = ({
    type,
    title,
    onClick,
    imageURLs,
}) => (
    <ContextSelectionItemButton
        onClick={onClick}
        appearance={ButtonAppearance.icon}
        data-e2e-class="context-selection-item"
    >
        <ItemProfileImage size={40} src={imageURLs} limitCount={3} tooltipAll={false} />
        <Base marginLeft={s3} overflow="hidden">
            <Body emphasized>{type}</Body>
            <Body whiteSpace="initial">{title}</Body>
        </Base>
    </ContextSelectionItemButton>
);

const ContextSelectionItemButton = styled(Button)`
    display: flex;
    align-items: center;

    width: 100%;
    padding: ${getSpacing([s2, s3])};
    border-radius: 12px;

    text-align: left;

    background-color: ${props => props.theme.mf.colorPalette.n50};

    /* This should be refactored into the button component at some point */
    &&:hover {
        background-color: ${props => props.theme.backgroundHover};
    }
    &&:active {
        background-color: ${props => props.theme.background};
    }
    &&:focus {
        border-color: ${props => props.theme.borderConfiguration.focus};
    }

    :not(:first-child) {
        margin-top: ${getSpacing(s2)};
    }
`;

/*
/----------------------------------------------
/ Main context challenge component
/----------------------------------------------
*/

const ChallengeBackdrop = styled(Flex)`
    height: 100vh;
    background: ${props => props.theme.mf.colorPalette.p50};
`;

const ChallengeContainer = styled(Flex)`
    justify-content: flex-start;
    background: ${props => props.theme.mf.colorPalette.n0};

    width: 100%;
    height: 100%;

    ${props => Breakpoints.tabletPortraitAndLarger`
        margin-top: 15%;

        width: 360px;
        max-height: calc(100% - 15% - 128px);
        height: unset;
        border-radius: ${props.theme.containerConfiguration.borderRadius};

        box-shadow: ${props.theme.elevation.six};
    `}

    ${Breakpoints.tabletLandscapeAndLarger`
        max-height: calc(100% - 64px);
    `}
`;

const ButtonContainer = styled(Flex)`
    box-sizing: border-box;

    padding-top: ${getSpacing(s4)};
    padding-top: calc(${getSpacing(s4)} + env(safe-area-inset-top));
    padding-right: ${getSpacing(s6)};
    padding-left: ${getSpacing(s6)};
    padding-bottom: ${getSpacing(s1)};
`;

const IconButton = styled(Button)`
    width: ${getSpacing(s9)};
    height: ${getSpacing(s9)};
    padding: 0;

    &&:focus {
        border-color: ${props => props.theme.borderConfiguration.focus};
    }
`;

const ImagePlaceHolder = styled.img`
    width: ${getSpacing(s20)};
    height: ${getSpacing(s20)};
    border-radius: 50%;
    flex-shrink: 0;
`;

const ScrollContainer = styled(Base)`
    flex-grow: 1;
    overflow-y: auto;
    width: 100%;

    ${Breakpoints.tabletPortraitAndLarger`
        max-height: 400px;
    `}
`;

const StyledTwoFactorInput = styled(TwoFactorInput)`
    flex-grow: 1;
    width: 100%;
    margin-top: ${getSpacing(s6)};
    margin-bottom: ${getSpacing(s6)};
    padding-left: ${getSpacing(s6)};
    padding-right: ${getSpacing(s6)};
    align-items: center;
    box-sizing: border-box;

    ${Breakpoints.tabletPortraitAndLarger`
        flex-grow: initial;
    `}
`;

const ContextSearchTextField = styled(TextField)`
    background-color: ${props => props.theme.mf.colorPalette.p50};

    /*
     * Setting border color to the same as background instead of removing the border
     * to avoid 1px jumping when the active state kicks in with its border.
     */
    border-color: ${props => props.theme.mf.colorPalette.p50};
`;

interface InputProps {
    challenge: ChallengeContext;
    onCancel: VoidFunction;
    redirectUrl?: string;
    loginTLD?: string;
    email: string;
}

type TwoFactorState = {show: false} | {show: true; twoFactorSubmitHandler: (twoFactorCode: string) => void};
type ErrorState = {show: false} | {show: true; title: string; details: string};

const ContextChallenge: React.FC<React.PropsWithChildren<InputProps>> = props => {
    const {challenge, onCancel, redirectUrl, loginTLD, email} = props;

    const [answerChallengeMutation] = useMutation<AnswerChallengeMutation, AnswerChallengeMutationVariables>(
        Queries.AnswerChallenge,
        {
            context: {
                skipAuthentication: true,
            },
            client: loginTLD ? getApolloClient(loginTLD) : undefined,
        },
    );

    const answerChallengeHandler = React.useCallback(
        (choice: InputProps['challenge']['choices'][number], twoFactorCode: string | null = null) =>
            answerChallengeMutation({
                variables: {
                    deviceId: challenge.deviceId,
                    twoFactorCode: twoFactorCode ? Number(twoFactorCode) : null,
                    userContextId: choice.context.id,
                    loginId: challenge.loginId,
                    hmac: choice.hmac,
                    expiresAt: challenge.expiresAt,
                },
            }).then(result => {
                if (!result?.data) {
                    return;
                }

                // For now we have to just clear the current route.
                // Otherwise, there's a chance that the selected context might be redirected
                // to a route that it doesn't have the permissions to visit.
                LocalStorage.clearKey('currentRoute');

                const challengeResult = result.data.me.answerChallenge;

                switch (challengeResult.__typename) {
                    case 'AuthenticationSucceeded': {
                        const {accessToken, deviceId} = challengeResult;
                        LoginHelpers.postLoginChores(deviceId);

                        if (loginTLD) {
                            ApiHelper.setApiConfig(loginTLD);
                        }

                        LoginActions.credentialLoginSuccess.dispatch({accessToken}, {redirectUrl});
                        return;
                    }
                    case 'AuthenticationFailed':
                        setErrorState({
                            show: true,
                            title: challengeResult.errorTitle,
                            details: challengeResult.errorDetails,
                        });
                        return;
                    default:
                        SentryService.captureMessage('Reached an invalid state when answering a context challenge', {
                            extra: {
                                challengeResult,
                            },
                        });
                        return;
                }
            }),
        [answerChallengeMutation, challenge.deviceId, challenge.expiresAt, challenge.loginId, loginTLD, redirectUrl],
    );

    const firstChoice = challenge.choices[0];

    const [twoFactorState, setTwoFactorState] = React.useState<TwoFactorState>(
        firstChoice && firstChoice.requiresTwoFactor && challenge.choices.length === 1
            ? {
                  show: true,
                  twoFactorSubmitHandler: (twoFactorCode: string) => {
                      answerChallengeHandler(firstChoice, twoFactorCode);
                  },
              }
            : {show: false},
    );

    const [errorState, setErrorState] = React.useState<ErrorState>({show: false});

    const handleSelect = React.useCallback(
        (choice: ChallengeChoice) => {
            if (choice.requiresTwoFactor) {
                setTwoFactorState({
                    show: true,
                    twoFactorSubmitHandler: (twoFactorCode: string) => {
                        answerChallengeHandler(choice, twoFactorCode);
                    },
                });
            } else {
                answerChallengeHandler(choice);
            }
        },
        [answerChallengeHandler],
    );

    const availableContexts = React.useMemo(() => {
        // Preserving the original "choice" while also mapping to the structure
        // that useFilteredUserContexts expects.
        return challenge.choices.map(choice => {
            return {
                ...choice.context,
                originalChoice: choice,
            };
        });
    }, [challenge.choices]);

    const {searchQuery, setSearchQuery, filteredContexts, showSearchField} = useFilteredUserContexts(availableContexts);

    return (
        <>
            <ChallengeBackdrop fullWidth justify="center" align="flex-start">
                <ChallengeContainer direction="column" align="center">
                    <ButtonContainer fullWidth>
                        <IconButton
                            appearance={ButtonAppearance.icon}
                            onClick={() => {
                                // When clicking back, if there is only one challenge choice,
                                // we need to skip the choices screen and redirect the user directly to the first login screen
                                if (twoFactorState.show && challenge.choices.length > 1) {
                                    setErrorState({show: false});
                                    setTwoFactorState({show: false});
                                } else {
                                    onCancel();
                                }
                            }}
                        >
                            <Backward size={20} />
                        </IconButton>
                    </ButtonContainer>
                    {errorState.show ? (
                        <Base
                            paddingLeft={s6}
                            paddingRight={s6}
                            boxSizing="border-box"
                            marginTop={s4}
                            marginBottom={s4}
                        >
                            <Notice
                                severity={NoticeSeverity.ERROR}
                                withHeader
                                title={errorState.title}
                                message={errorState.details}
                            />
                        </Base>
                    ) : null}

                    {twoFactorState.show ? (
                        <StyledTwoFactorInput handleSubmit={twoFactorState.twoFactorSubmitHandler} />
                    ) : (
                        <>
                            <Flex
                                direction="column"
                                align="center"
                                marginBottom={s8}
                                boxSizing="border-box"
                                paddingLeft={s4}
                                paddingRight={s4}
                            >
                                <ImagePlaceHolder
                                    src={challenge.person.profileImage?.url ?? 'img/empty-states/profile-image.svg'}
                                />
                                <Subheader emphasized marginTop={s2} textAlign="center">
                                    {challenge.person.name.fullName}
                                </Subheader>
                                <Body textAlign="center">{email}</Body>
                            </Flex>
                            <Flex
                                direction="column"
                                paddingLeft={s6}
                                paddingRight={s6}
                                paddingBottom={s6}
                                fullWidth
                                boxSizing="border-box"
                                overflow="auto"
                            >
                                {showSearchField ? (
                                    <ContextSearchTextField
                                        value={searchQuery}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            setSearchQuery(e.target.value)
                                        }
                                        placeholder={i18next.t('search')}
                                    />
                                ) : null}
                                <ScrollContainer boxSizing="border-box">
                                    {filteredContexts.map((context, i) => {
                                        return (
                                            <ContextSelectionItem
                                                key={`${context.id}_${i}`}
                                                onClick={() => handleSelect(context.originalChoice)}
                                                title={getTitleForTarget(context.target)}
                                                imageURLs={getImageUrlForTarget(context.target)}
                                                type={getTypeForTarget(context.target)}
                                            />
                                        );
                                    })}
                                </ScrollContainer>
                            </Flex>
                        </>
                    )}
                </ChallengeContainer>
            </ChallengeBackdrop>
        </>
    );
};

type ChallengeChoice = ChallengeContext['choices'][0];
type ChallengeChoiceTarget = ChallengeChoice['context']['target'];

const getTypeForTarget = (target: ChallengeChoiceTarget) => {
    switch (target.__typename) {
        case 'InstitutionSet':
            return i18next.t('employee');
        case 'PersonContextTarget':
            return i18next.t('userContexts.childrensAccount');
        default:
            return '-';
    }
};

export default ContextChallenge;
